import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import PlayModal from "./components/PlayModal";
import EpisodeList from "./components/EpisodeList";
import { getRelated, getVideoById } from "@/utils/video";
import { formatRating, formatViews } from "@/utils/format";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FAV_KEY = "online-play:favorites";

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const video = id ? getVideoById(id) : undefined;

  const [playOpen, setPlayOpen] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [toast, setToast] = useState("");
  const [episode, setEpisode] = useState(1);

  useEffect(() => {
    setFavorites(readFavorites());
    setEpisode(1);
  }, [id]);

  useDocumentMeta({
    title: video ? `${video.title} - 视频详情 - 在线观看平台` : "视频详情 - 在线播放",
    description: video
      ? `在线播放为您提供高清“${video.title}”视频及详细介绍，畅享高品质观影体验。`
      : undefined,
  });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!video) {
    return (
      <div className="pt-16 md:pt-20 px-4 md:px-8 flex flex-col items-center justify-center py-28">
        <span className="w-16 h-16 rounded-full bg-background-200/70 flex items-center justify-center">
          <i className="ri-emotion-sad-line text-3xl text-foreground-500" />
        </span>
        <p className="mt-4 text-foreground-800 font-medium">未找到该视频</p>
        <Link
          to="/"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium whitespace-nowrap bg-primary-500 text-background-50 cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const isFav = favorites.includes(video.id);
  const related = getRelated(video, 6);

  const toggleFav = () => {
    const next = isFav ? favorites.filter((f) => f !== video.id) : [...favorites, video.id];
    setFavorites(next);
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
    } catch {
      /* ignore storage errors */
    }
    setToast(isFav ? "已取消收藏" : "已加入收藏（仅本地演示）");
  };

  const share = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}detail/${video.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快分享给好友吧");
    } catch {
      setToast(url);
    }
  };

  const episodeLabel = video.episodeCount ? `第 ${episode} 集` : video.duration;

  return (
    <div className="pt-16 md:pt-20">
      {/* Hero band */}
      <div className="relative h-[380px] md:h-[460px] overflow-hidden">
        <img
          src={video.backdrop || video.poster}
          alt={`${video.title}背景图`}
          title={`${video.title} - 高清视频详情`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/40" />

        <div className="relative z-10 h-full flex items-end">
          <div className="w-full px-4 md:px-8 pb-8 md:pb-10">
            <nav className="flex items-center flex-wrap gap-1.5 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to={`/category/${video.type}`} className="hover:text-accent-500 transition-colors cursor-pointer">
                {video.typeLabel}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-700 truncate max-w-[160px]">{video.title}</span>
            </nav>

            <div className="flex flex-col sm:flex-row gap-5 md:gap-7">
              <img
                src={video.poster}
                alt={`${video.title}海报`}
                title={video.title}
                className="w-28 md:w-40 aspect-[2/3] object-cover object-top rounded-lg shrink-0 border border-background-300/50"
              />
              <div className="min-w-0">
                <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 text-shadow-hero">
                  {video.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-sm md:text-base text-foreground-700">
                  <span className="flex items-center gap-1 font-semibold text-accent-500">
                    <i className="ri-star-fill" /> {formatRating(video.rating)}
                  </span>
                  <span>{video.typeLabel}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-500" />
                  <span>{video.year}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-500" />
                  <span>{video.region}</span>
                  {video.episodeCount ? (
                    <>
                      <span className="w-1 h-1 rounded-full bg-foreground-500" />
                      <span>全 {video.episodeCount} 集</span>
                    </>
                  ) : (
                    <>
                      <span className="w-1 h-1 rounded-full bg-foreground-500" />
                      <span>{video.duration}</span>
                    </>
                  )}
                  <span className="w-1 h-1 rounded-full bg-foreground-500" />
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" /> {formatViews(video.views)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {video.genres.map((g) => (
                    <Link
                      key={g}
                      to={`/search?q=${encodeURIComponent(g)}`}
                      className="px-3 py-1 rounded-full text-xs bg-secondary-500/20 text-secondary-300 hover:bg-secondary-500/30 transition-colors cursor-pointer"
                    >
                      {g}
                    </Link>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-5">
                  <button
                    type="button"
                    onClick={() => setPlayOpen(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer animate-pulse-play"
                  >
                    <i className="ri-play-fill text-lg" /> 立即播放
                  </button>
                  <button
                    type="button"
                    onClick={toggleFav}
                    className={`inline-flex items-center gap-2 px-5 py-3 rounded-md font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      isFav
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200/80 text-foreground-950 hover:bg-background-200"
                    }`}
                  >
                    <i className={isFav ? "ri-heart-3-fill text-lg" : "ri-heart-3-line text-lg"} />
                    {isFav ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={share}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-medium whitespace-nowrap bg-background-200/80 text-foreground-950 hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-share-forward-line text-lg" /> 分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
          <div className="lg:col-span-2">
            {/* Player placeholder */}
            <div className="relative rounded-lg overflow-hidden bg-background-100 border border-background-300/50 aspect-video">
              <img
                src={video.backdrop || video.poster}
                alt={`${video.title}播放封面`}
                title={video.title}
                className="absolute inset-0 w-full h-full object-cover object-top opacity-50"
              />
              <div className="absolute inset-0 bg-background-50/60" />
              <button
                type="button"
                onClick={() => setPlayOpen(true)}
                className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer"
              >
                <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500 flex items-center justify-center">
                  <i className="ri-play-fill text-3xl md:text-4xl text-background-50" />
                </span>
                <span className="mt-3 text-sm text-foreground-800">点击播放 · {episodeLabel}</span>
              </button>
            </div>

            {/* Summary */}
            <section className="mt-8">
              <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{video.summary}</p>
            </section>

            {video.episodeCount ? (
              <EpisodeList total={video.episodeCount} current={episode} onSelect={setEpisode} />
            ) : null}
          </div>

          {/* Sidebar info */}
          <aside>
            <div className="rounded-lg bg-background-100 border border-background-300/50 p-5 md:p-6">
              <h3 className="font-heading font-semibold text-base text-foreground-950 mb-4">视频信息</h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">类别</dt>
                  <dd className="text-foreground-900">{video.typeLabel}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">年份</dt>
                  <dd className="text-foreground-900">{video.year}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">地区</dt>
                  <dd className="text-foreground-900">{video.region}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">评分</dt>
                  <dd className="text-accent-500 font-semibold">{formatRating(video.rating)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">{video.episodeCount ? "集数" : "片长"}</dt>
                  <dd className="text-foreground-900">
                    {video.episodeCount ? `全 ${video.episodeCount} 集` : video.duration}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">播放量</dt>
                  <dd className="text-foreground-900">{formatViews(video.views)}</dd>
                </div>
              </dl>
              <p className="mt-5 pt-4 border-t border-background-300/40 text-xs text-foreground-500 leading-relaxed">
                提示：本站为演示站点，播放、下载与付费功能均未开放，收藏仅保存在本地浏览器。
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate(`/category/${video.type}`)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-medium whitespace-nowrap bg-background-200/70 text-foreground-950 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-grid-line" /> 查看更多{video.typeLabel}
            </button>
          </aside>
        </div>

        {/* Related */}
        <section className="mt-12 md:mt-16">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">相关推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
            {related.map((r, i) => (
              <VideoCard key={r.id} video={r} index={i} />
            ))}
          </div>
        </section>
      </div>

      <PlayModal
        open={playOpen}
        title={video.title}
        episodeLabel={video.episodeCount ? `第 ${episode} 集` : undefined}
        onClose={() => setPlayOpen(false)}
      />

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-md bg-background-200 text-foreground-950 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}